"use client";

import React, { useEffect, useState } from "react";
import { 
  Calendar, 
  User, 
  Clock, 
  Share2, 
  Tag, 
  ArrowRight, 
  Facebook, 
  Twitter, 
  Linkedin 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";



function SocialShareButton({ icon: Icon, label }: { icon: any, label: string }) {
  return (
    <Button variant="outline" size="icon" className="rounded-full hover:text-primary hover:border-primary transition-colors" aria-label={`Share on ${label}`}>
      <Icon className="h-4 w-4" />
    </Button>
  );
}



function RelatedPostsList() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="5190" className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map((_, i) => (
          <div key={i} data-index={i} className="h-80 rounded-xl bg-muted/50 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="group overflow-hidden border-none shadow-sm hover:shadow-xl transition-all duration-300 bg-card h-full flex flex-col rounded-xl">
          <div className="relative w-full overflow-hidden aspect-[16/10]">
            {post.featuredImage ? (
              <img 
                src={post.featuredImage} 
                alt={post.title}
                className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
              />
            ) : (
              <div className="w-full h-full bg-muted flex items-center justify-center text-muted-foreground">
                <span className="text-sm font-default">No Image</span>
              </div>
            )}
            <div className="absolute top-4 left-4">
               <span className="bg-secondary text-secondary-foreground text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                 {post.categories?.[0]?.name || "Community"}
               </span>
            </div>
          </div>
          
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
              <Calendar className="h-3 w-3" />
              <span>{post.date ? new Date(post.date).toLocaleDateString() : "Recent"}</span>
            </div>
            <CardTitle className="font-default text-xl leading-tight group-hover:text-primary transition-colors line-clamp-2">
              <Link to={post.link || "#"}>
                {post.title}
              </Link>
            </CardTitle>
          </CardHeader>
          
          <CardContent className="pb-4 flex-grow">
            <CardDescription className="line-clamp-3 text-sm font-default">
              {post.excerpt ? (
                <div dangerouslySetInnerHTML={{ __html: post.excerpt }} />
              ) : (
                "Learn more about how your donations are helping the Ottawa community..."
              )}
            </CardDescription>
          </CardContent>
          
          <CardFooter className="pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="ghost" className="w-full justify-between group-hover:bg-primary/5 text-primary hover:text-primary rounded-lg">
                Read More <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function RelatedPostsSection({ currentPostId }: { currentPostId?: string }) {
  
  
  const queryArgs: any = {
    per_page: 3,
    orderby: "date",
    order: "desc"
  };

  if (currentPostId) {
    queryArgs.exclude = [parseInt(currentPostId)];
  }

  const relatedQuery = new WP_Query(queryArgs);

  return (
    <div className="mt-16 pt-12 border-t border-border">
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-2xl md:text-3xl font-default font-bold text-primary">
          Community Updates
        </h3>
        <Link to="#">
          <Button variant="outline" className="hidden md:flex rounded-full">View All Updates</Button>
        </Link>
      </div>
      
      <PostsProvider wp_query={relatedQuery}>
        <RelatedPostsList />
      </PostsProvider>
      
      <div className="mt-8 md:hidden">
        <Link to="#" className="w-full block">
          <Button variant="outline" className="w-full rounded-full">View All Updates</Button>
        </Link>
      </div>
    </div>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-8">
        <div className="h-12 w-3/4 bg-muted/50 animate-pulse rounded-lg" />
        <div className="h-6 w-1/2 bg-muted/50 animate-pulse rounded-lg" />
        <div className="aspect-video w-full bg-muted/50 animate-pulse rounded-xl" />
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted/50 animate-pulse rounded" />
          <div className="h-4 w-full bg-muted/50 animate-pulse rounded" />
          <div className="h-4 w-2/3 bg-muted/50 animate-pulse rounded" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-4">
        <div className="bg-destructive/10 p-4 rounded-full mb-4">
          <Share2 className="h-8 w-8 text-destructive" />
        </div>
        <h2 className="text-2xl font-default font-bold text-foreground mb-2">Unable to load post</h2>
        <p className="text-muted-foreground mb-6 max-w-md font-default">
          We encountered an error while loading this content. Please try again later.
        </p>
        <Button onClick={() => refetch()} className="rounded-full">Try Again</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center px-4">
        <h2 className="text-2xl font-default font-bold text-foreground mb-2">Post not found</h2>
        <p className="text-muted-foreground mb-6 font-default">
          The article you are looking for does not exist or has been moved.
        </p>
        <Link to="#">
          <Button className="rounded-full">Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      {}
      <header className="mb-10 text-center md:text-left">
        <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mb-6">
          {post.categories?.map((cat, i) => (
            <span 
              key={i} 
              data-index={i}
              className="bg-secondary/20 text-secondary-foreground px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide border border-secondary/30"
            >
              {cat.name}
            </span>
          ))}
        </div>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-primary leading-tight mb-6">
          {post.title}
        </h1>

        <div className="flex flex-col md:flex-row items-center md:justify-between gap-6 py-6 border-y border-border/60">
          <div className="flex items-center gap-4">
            <Avatar className="h-12 w-12 border-2 border-background shadow-sm">
              <AvatarImage src={post.author?.avatar?.['96']} alt={post.author?.name} />
              <AvatarFallback className="bg-secondary text-secondary-foreground"><User className="h-5 w-5" /></AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-sm font-semibold text-foreground font-default">
                {post.author?.name || "Second Chance Team"}
              </p>
              <div className="flex items-center gap-3 text-xs text-muted-foreground font-default">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {post.date ? new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : "Recently Published"}
                </span>
                <span className="w-1 h-1 rounded-full bg-muted-foreground/40" />
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  4 min read
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground mr-2 hidden md:inline-block font-default">Share:</span>
            <SocialShareButton icon={Facebook} label="Facebook" />
            <SocialShareButton icon={Twitter} label="Twitter" />
            <SocialShareButton icon={Linkedin} label="LinkedIn" />
            <SocialShareButton icon={Share2} label="Copy Link" />
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-12 rounded-2xl overflow-hidden shadow-xl ring-1 ring-border/50">
          <AspectRatio ratio={16 / 9}>
            <img
              src={post.featuredImage}
              alt={post.title}
              className="object-cover w-full h-full"
            />
          </AspectRatio>
          {}
          <div className="bg-muted/30 px-4 py-2 text-xs text-muted-foreground text-center italic border-t border-border/50 font-default">
            Simplifying clothing donations to support local Ottawa charities and reduce waste.
          </div>
        </div>
      )}

      {}
      <div className="prose prose-lg prose-slate dark:prose-invert max-w-none 
        prose-headings:font-default prose-headings:text-primary 
        prose-a:text-secondary-foreground prose-a:no-underline prose-a:border-b-2 prose-a:border-secondary/50 hover:prose-a:border-secondary prose-a:transition-colors
        prose-img:rounded-xl prose-img:shadow-lg
        prose-blockquote:border-l-secondary prose-blockquote:bg-secondary/5 prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-blockquote:not-italic
        mb-12 font-default">
        {post.content ? (
          <div dangerouslySetInnerHTML={{ __html: post.content }} />
        ) : (
          <div className="space-y-4 text-muted-foreground">
            <p>
              At Second Chance Threads, we believe that every piece of clothing deserves a second life. Our mission in Ottawa is to make the donation process as simple as stepping onto your front porch. By providing free, no-contact collection, we help our neighbors declutter responsibly while supporting local organizations.
            </p>
            <h3>Making a Local Difference</h3>
            <p>
              When you book a pickup with us, you're not just clearing space in your closet; you're contributing to a cycle of sustainability. Our team carefully sorts every item collected, ensuring that high-quality clothing reaches those who need it most through our network of local charity partners.
            </p>
            <blockquote className="text-primary font-semibold">
              "Small acts of giving, when multiplied by a community, can transform the world starting right here in Ottawa."
            </blockquote>
            <p>
              From Kanata to Nepean and across downtown, we are committed to being your reliable partner in community giving. Together, we can reduce textile waste in our landfills and provide essential resources to our fellow residents.
            </p>
          </div>
        )}
      </div>

      {}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 py-8 border-t border-border">
        <div className="flex flex-wrap gap-2">
          <Tag className="h-4 w-4 text-muted-foreground mt-1" />
          {post.tags && post.tags.length > 0 ? (
            post.tags.map((tag, i) => (
              <Link key={i} to={tag.link || "#"}>
                <span 
                  data-index={i}
                  className="text-sm text-muted-foreground hover:text-primary hover:underline transition-colors cursor-pointer font-default"
                >
                  #{tag.name}
                </span>
              </Link>
            ))
          ) : (
            <span className="text-sm text-muted-foreground font-default">#OttawaDonations #SustainableLiving</span>
          )}
        </div>
        
        <div className="flex items-center gap-4">
           <span className="text-sm font-medium text-muted-foreground font-default">Was this helpful?</span>
           <div className="flex gap-2">
             <Button variant="outline" size="sm" className="h-8 rounded-md">Yes</Button>
             <Button variant="outline" size="sm" className="h-8 rounded-md">No</Button>
           </div>
        </div>
      </div>

      {}
      <div className="bg-muted/30 rounded-xl p-6 md:p-8 flex flex-col md:flex-row gap-6 items-center md:items-start text-center md:text-left border border-border mt-12">
        <Avatar className="h-20 w-20 md:h-24 md:w-24 border-4 border-background shadow-md">
          <AvatarImage src={post.author?.avatar?.['96']} alt={post.author?.name} />
          <AvatarFallback className="text-2xl bg-secondary text-secondary-foreground font-default">
            {post.author?.name?.charAt(0) || "S"}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <h3 className="text-lg font-default font-bold text-primary mb-1">About {post.author?.name || "Our Mission"}</h3>
          <p className="text-sm text-muted-foreground mb-4 font-default">
            {post.author?.description || "The team at Second Chance Threads is dedicated to making clothing donation simple, accessible, and impactful for the entire Ottawa community. We focus on reducing waste and supporting local charities through convenient porch pickups."}
          </p>
          <Link to={post.author?.link || "#"}>
            <Button variant="link" className="p-0 h-auto text-secondary-foreground font-semibold font-default">
              View all updates from {post.author?.name || "Second Chance"} <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </Link>
        </div>
      </div>

      {}
      <RelatedPostsSection currentPostId={post.id} />
    </article>
  );
}



export default function SinglePostTemplateSection() {
  
  
  
  
  let currentWPQueryParams = {};
  if (typeof window !== 'undefined' && (window as any).wvcClient) {
    currentWPQueryParams = (window as any).wvcClient.getCurrentWPQueryParams();
  } else {
    
    currentWPQueryParams = { p: 1 }; 
  }

  
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background min-h-screen w-full text-foreground">
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}